import { TASK_CONTEXT_TYPE_LABELS, parseMentionTokens, taskContextUrl } from '@shared/task-contexts';
import type { TaskAttachment, TaskBoard, TaskContext, TaskDetail, TaskMember } from '@shared/types/tasks';
import { formatBytes, formatDateTime } from '@/lib/formatters';
import { memberName } from './task-ui';
import { taskPerson } from './task-people';
import { formatTaskNumber } from '@shared/task-number';

type Meta = Pick<TaskBoard, 'members' | 'labels' | 'columns'>;

type Options = {
  /** `https://jflo.app`: every link in the Markdown is absolute so it works pasted anywhere. */
  origin: string;
  formatTime?: (date: Date) => string;
};

const absolute = (origin: string, path: string) => (/^https?:\/\//.test(path) ? path : `${origin}${path}`);

/** Stored text as Markdown: people as `@Name`, linked things as links to their jflo page. */
function mentionMarkdown(text: string, members: TaskMember[], contexts: TaskContext[], origin: string): string {
  let out = '';
  let cursor = 0;
  for (const token of parseMentionTokens(text)) {
    out += text.slice(cursor, token.start);
    if (token.target.kind === 'user') {
      const id = token.target.id;
      const member = members.find((m) => m.user_id === id);
      out += `@${member ? memberName(member) : token.label}`;
    } else if (token.target.kind === 'ai') {
      out += `@${token.label}`;
    } else {
      const { ref } = token.target;
      const known = contexts.find((c) => c.type === ref.type && c.id === ref.id);
      out += `[@${token.label}](${absolute(origin, known?.url || taskContextUrl(ref))})`;
    }
    cursor = token.end;
  }
  return out + text.slice(cursor);
}

function attachmentLine(a: TaskAttachment, origin: string): string {
  const url = absolute(origin, a.url);
  return a.is_image ? `![${a.filename}](${url})` : `[${a.filename}](${url}) (${formatBytes(a.size)})`;
}

/** When the earliest signed file link stops working, or null if there are no files. */
function linksExpireAt(tasks: TaskDetail[]): Date | null {
  const expiries = tasks
    .flatMap((t) => t.events.flatMap((e) => e.attachments))
    .map((a) => Number(new URL(a.url, 'http://x').searchParams.get('exp')))
    .filter((exp) => exp > 0);
  return expiries.length ? new Date(Math.min(...expiries) * 1000) : null;
}

/** One task's details, description and chat. */
function taskSection(task: TaskDetail, meta: Meta, origin: string, formatTime: (date: Date) => string): string {
  const person = (id: string | null) => memberName(taskPerson(meta.members, id));
  const column = meta.columns.find((c) => c.id === task.column_id);
  const labels = task.label_ids.flatMap((id) => meta.labels.find((l) => l.id === id)?.name ?? []);
  const text = (body: string) => mentionMarkdown(body, meta.members, task.contexts, origin);

  const details = [
    `- **Task:** ${origin}/tasks/${task.id}`,
    `- **Column:** ${column?.name ?? 'Unknown'}${task.archived ? ' (archived)' : ''}`,
    task.assignee_ids.length ? `- **Assigned:** ${task.assignee_ids.map(person).join(', ')}` : null,
    labels.length ? `- **Labels:** ${labels.join(', ')}` : null,
    task.tags.length ? `- **Tags:** ${task.tags.map((t) => `#${t}`).join(' ')}` : null,
    `- **Created:** ${formatTime(new Date(task.created_at))}${task.created_by_user_id ? ` by ${person(task.created_by_user_id)}` : ''}`,
    ...(task.contexts.length
      ? [
          '- **Linked:**',
          ...task.contexts.map(
            (c) => `  - ${TASK_CONTEXT_TYPE_LABELS[c.type]}: [${c.title}](${absolute(origin, c.url || taskContextUrl(c))})`
          ),
        ]
      : []),
  ].filter((line) => line !== null);

  const messages = task.events
    .filter((e) => e.kind === 'chat')
    .map((e) => {
      const heading = `### ${person(e.actor_user_id)} · ${formatTime(new Date(e.created_at))}${e.edited_at ? ' (edited)' : ''}`;
      const files = e.attachments.map((a) => attachmentLine(a, origin));
      return [heading, e.body?.trim() ? text(e.body.trim()) : null, files.length ? files.join('\n\n') : null]
        .filter((part) => part !== null)
        .join('\n\n');
    });

  return [
    // The Task number lets an AI given this Markdown find the task through the API.
    `# ${task.number ? `${formatTaskNumber(task.number)} · ` : ''}${task.title}`,
    details.join('\n'),
    `## Description\n\n${task.description.trim() ? text(task.description.trim()) : '_No description._'}`,
    messages.length ? `## Chat\n\n${messages.join('\n\n')}` : null,
  ]
    .filter((s) => s !== null)
    .join('\n\n');
}

/**
 * Tasks as Markdown, for pasting into an AI chat or a doc: each task's details, description
 * and chat with its files, separated by horizontal rules. Files are signed links that open
 * without signing in, for about an hour, so load the tasks fresh just before calling this.
 * Change lines ("moved to Next") are left out.
 */
export function tasksToMarkdown(tasks: TaskDetail[], meta: Meta, { origin, formatTime = formatDateTime }: Options): string {
  const expires = linksExpireAt(tasks);
  const parts = tasks.map((task) => taskSection(task, meta, origin, formatTime));
  if (expires) parts.push(`_Files are signed links that open without signing in, until ${formatTime(expires)}._`);
  return `${parts.join('\n\n---\n\n')}\n`;
}

export function taskToMarkdown(task: TaskDetail, meta: Meta, options: Options): string {
  return tasksToMarkdown([task], meta, options);
}
